import React, { useId } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import useHighlight, { globalSearchMarkStyle } from "data/hooks/useHighlight";
import { useGlobalSearchContext } from "../global-search/SearchProvider";
import { formatDateTimeInUserTimeZone } from "utils/date";
import DotDivider from "ui/components/data-display/DotDivider";
import { Rating } from "data/@types/Rating";

type ActivityInfoProps = {
  author?: string | null;
  date: string;
  entityType: string;
  lastRating: Rating | null;
  priorRating: Rating | null;
  rating?: Rating;
};

const ActivityInfo = ({
  author,
  date,
  entityType,
  lastRating,
  priorRating,
  rating,
}: ActivityInfoProps) => {
  const id = useId();
  const { search } = useGlobalSearchContext();
  const { highlight } = useHighlight(search, globalSearchMarkStyle);
  const isProductOrStrategy = ["Product", "Strategy"].includes(entityType);
  const dateForTooltip = date
    ? formatDateTimeInUserTimeZone(date, { showTimeZoneCode: true })
    : "--";
  const shortDate = dateForTooltip.substring(0, 10);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        color: "text.secondary",
        pt: "1px",
        flexWrap: "wrap",
      }}
      data-test-id="activity-info"
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: "11px", textWrap: "nowrap" }}
        aria-label={`author: ${author}`}
        dangerouslySetInnerHTML={{
          __html: author ? highlight(author) : "--",
        }}
      />

      <DotDivider style={{ mx: 1 }} />

      <Typography id={`date-${id}`} sx={{ fontSize: "11px" }}>
        Date:&nbsp;
      </Typography>
      <Tooltip title={`published on ${dateForTooltip}`}>
        <time
          aria-labelledby={`date-${id}`}
          style={{ padding: 0, fontSize: "11px", textWrap: "nowrap" }}
          dangerouslySetInnerHTML={{
            __html: highlight(shortDate),
          }}
          dateTime={dateForTooltip}
        />
      </Tooltip>

      {isProductOrStrategy && (
        <>
          <DotDivider style={{ mx: 1 }} />

          {priorRating !== undefined ? (
            <>
              <Typography id={`rc-${id}`} sx={{ fontSize: "11px" }}>
                Rating change
              </Typography>
              <DotDivider style={{ mx: 1 }} />
              <Tooltip title="Rating Change">
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "info.ratingChange",
                    border: "1px solid",
                    borderColor: "info.ratingChange",
                    borderRadius: "2px",
                    px: "3px",
                    alignSelf: "center",
                    fontSize: "11px",
                    textWrap: "nowrap",
                  }}
                  aria-labelledby={`rc-${id}`}
                >
                  {priorRating || "--"} {"\u2192"} {rating || "--"}
                </Typography>
              </Tooltip>
            </>
          ) : (
            <>
              <Typography id={`r-${id}`} sx={{ fontSize: "11px" }}>
                Rating:&nbsp;
              </Typography>
              <Typography
                aria-labelledby={`r-${id}`}
                sx={{ fontSize: "11px" }}
                dangerouslySetInnerHTML={{
                  __html: lastRating ? highlight(lastRating) : "--",
                }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default ActivityInfo;
